import { createContext, forwardRef, memo, useContext } from 'react';

const createContextWithHooks = <TValue>(name: string, initialValue?: TValue) => {
  const Context = createContext<TValue | undefined>(initialValue);

  const useContextOptionalHook = () => useContext(Context);

  const useContextHook = () => {
    const ctxValue = useContextOptionalHook();

    if (ctxValue === undefined)
      throw new Error(
        `useContext - ${name}: consuming a context outside its intended provider will always result in undefined`,
      );

    return ctxValue;
  };

  return {
    Context,
    Provider: Context.Provider,
    useContext: useContextHook,
    useContextOptional: useContextOptionalHook,
  };
};

const typedForwardRef = <TRefValue, TProps>(
  renderFn: (props: React.PropsWithoutRef<TProps>, ref: React.Ref<TRefValue>) => React.JSX.Element,
  displayName: string,
) => {
  const ForwardRefComponent = forwardRef(renderFn);

  ForwardRefComponent.displayName = displayName;

  return ForwardRefComponent as (
    props: TProps & React.RefAttributes<TRefValue>,
  ) => React.JSX.Element;
};

const typedMemo = memo as <T extends React.ElementType>(
  Component: T,
  propsAreEqual?: (
    prevProps: Readonly<React.ComponentProps<T>>,
    nextProps: Readonly<React.ComponentProps<T>>,
  ) => boolean,
) => T;

export { createContextWithHooks, typedForwardRef, typedMemo };
